import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { getTimeRangeWithDates } from '@ubeya/shared/utils/time';
import { Button, FormModal as BaseFormModal, PromptClick } from '@ubeya/shared-web/components';
import { API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import BreakModalFields from './BreakModalFields';
import { checkOverlappingHours } from 'scheduling/utils';

const FormModal = styled(BaseFormModal)`
  .delete-button {
    margin-right: auto;

    &::after {
      content: '${({ $deleteText }) => $deleteText}';
    }
  }
`;

const DeleteButton = styled(Button).attrs(() => ({ className: 'delete-button' }))``;

const BreakModal = ({
  onClose,
  onConfirm,
  onDelete,
  date,
  breaktimes: allBreaktimes,
  selectedBreak,
  branchId,
  startTime: actualIn,
  endTime: actualOut
}) => {
  const { t } = useContext(ConfigContext);

  const { startTime, endTime } = selectedBreak || {};

  const { mappedBranches } = useBranches();

  const breakTypes = useMemo(() => mappedBranches?.[branchId]?.timesheetConfig?.breakTypes || [], [
    branchId,
    mappedBranches
  ]);

  const initalValues = useMemo(
    () => ({
      ...selectedBreak,
      breakDuration:
        startTime && endTime && moment.duration(moment(endTime).diff(startTime, 'milliseconds')).asMinutes()
    }),
    [endTime, selectedBreak, startTime]
  );

  const breaktimes = useMemo(
    () => (selectedBreak?.id ? allBreaktimes.filter(({ id }) => id !== selectedBreak?.id) : allBreaktimes),
    [allBreaktimes, selectedBreak?.id]
  );

  const handleSubmit = useCallback(
    async (values) => {
      const { startTime: timeRangeStartTime, endTime: timeRangeEndTime } = getTimeRangeWithDates({
        date,
        startTime: values.startTime,
        endTime: values.endTime
      });

      const breakTypeId = !!initalValues.breakTypeId && !values.breakTypeId ? null : values.breakTypeId || undefined;
      await onConfirm({
        startTime: timeRangeStartTime,
        endTime: timeRangeEndTime,
        comments: values.comments,
        breakTypeId
      });

      onClose();
    },
    [date, onConfirm, initalValues, onClose]
  );

  const handleDelete = useCallback(async () => {
    await onDelete();
    onClose();
  }, [onClose, onDelete]);

  const validate = useCallback(
    ({ startTime: startTimeValue, endTime: endTimeValue, breakDuration }) => {
      const errors = {};

      if (moment(startTimeValue).isAfter(moment(endTimeValue))) {
        errors.startTime = t('afterBreakEndTime');
        return errors;
      }

      const { startTime: timeRangeStartTime, endTime: timeRangeEndTime } = getTimeRangeWithDates({
        date,
        startTime: startTimeValue,
        endTime: endTimeValue
      });

      const newStart = moment(timeRangeStartTime, API_DATE_TIME_FORMAT);
      const newEnd = moment(timeRangeEndTime, API_DATE_TIME_FORMAT);

      if (Number(breakDuration) === 0) {
        errors.breakDuration = t('breakTimeCanNotBeZero');
      }

      //overalpping with other breaks
      if (checkOverlappingHours({ data: breaktimes, newStart, newEnd, fieldName: 'start', includeEdges: false })) {
        errors.startTime = t('overlapping');
      }

      if (checkOverlappingHours({ data: breaktimes, newStart, newEnd, fieldName: 'end', includeEdges: false })) {
        errors.endTime = t('overlapping');
      }

      if (newStart.isBefore(moment(actualIn, API_DATE_TIME_FORMAT))) {
        newStart.add(1, 'day'); // Add one day to breakStart
      }

      if (newEnd.isBefore(moment(actualIn, API_DATE_TIME_FORMAT))) {
        newEnd.add(1, 'day');
      }

      if (newStart.isSameOrAfter(moment(actualOut, API_DATE_TIME_FORMAT))) {
        errors.startTime = t('afterEndTime');
      }

      if (newEnd.isAfter(moment(actualOut, API_DATE_TIME_FORMAT))) {
        errors.endTime = t('afterEndTime');
      }

      if (newEnd.isSameOrBefore(moment(actualIn, API_DATE_TIME_FORMAT))) {
        errors.endTime = t('beforeStartTime');
      }

      return errors;
    },
    [breaktimes, date, actualIn, actualOut, t]
  );

  return (
    <FormModal
      initialValues={initalValues}
      onSubmit={handleSubmit}
      size="small"
      validate={validate}
      title={selectedBreak.id ? t('editBreak') : t('addBreak')}
      confirmText={selectedBreak.id ? 'update' : 'add'}
      disabledOnPristine={false}
      onClose={onClose}
      $deleteText={t('delete')}
      renderButtons={() =>
        selectedBreak.id && (
          <PromptClick onClick={handleDelete} body="areYouSureDeleteBreak" confirmText="yes" usePortal>
            <DeleteButton type="button" secondary />
          </PromptClick>
        )
      }>
      <BreakModalFields t={t} actualIn={actualIn} date={date} breakTypes={breakTypes} selectedBreak={selectedBreak} />
    </FormModal>
  );
};

export default BreakModal;
